import { useCallback, useEffect, useRef, useState } from 'react';
import client from '../api/client';

// Loads the customer list (the user only sees customers they're allowed to)
// and resolves a customer's effective VitalEngine facility ids on demand.
// Resolved id sets are cached per customer to avoid re-hitting the API when
// the user toggles back and forth.
//
// Customers come back with master/sub-deal structure; we sort them so that
// each master is followed by its sub-deals, indented in the label, which
// makes the dropdown easier to scan.
export function useCustomerFilter() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const resolveCache = useRef(new Map());

  // Union of every customer's resolved facility IDs, used by the Insights
  // page's Spokes tab to identify non-customer (free-account) activity.
  // Loaded once on mount via /all-customer-facility-ids (single round-trip
  // vs. N per-customer calls). `null` means "not loaded yet" so callers
  // can show a loading state instead of mistreating an empty Set as
  // "everything is a spoke" while the request is in flight.
  const [allCustomerFacilityIds, setAllCustomerFacilityIds] = useState(null);

  // Map<facilityIdString, masterCustomerId(int)>. Used by analytics surfaces
  // (e.g. CollaborationAnalytics) to classify a record as internal vs.
  // external relative to the receiving facility's customer hierarchy.
  // `null` means "not loaded yet" so callers can wait before classifying.
  const [facilityToMasterCustomerId, setFacilityToMasterCustomerId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await client.get('/api/customers');
        if (!cancelled && res.data?.customers) {
          setCustomers(sortCustomers(res.data.customers));
        }
      } catch {
        // Customers feature may not be reachable for this role — fail silent.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await client.get('/api/customers/all-customer-facility-ids');
        if (cancelled) return;
        const ids = (res.data?.facilityIds || []).map(String);
        setAllCustomerFacilityIds(new Set(ids));
      } catch {
        // Endpoint may not be reachable for this role; leave as null and
        // the Spokes tab will show a "couldn't load" state.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await client.get('/api/customers/facility-master-map');
        if (cancelled) return;
        const raw = res.data?.facilityToMasterCustomerId || {};
        // The map's values are integers but Map keys must be strings to
        // match what facility ids look like in cached records (we always
        // String(...) those before lookup).
        const m = new Map();
        for (const [k, v] of Object.entries(raw)) {
          m.set(String(k), Number(v));
        }
        setFacilityToMasterCustomerId(m);
      } catch {
        // Same fail-silent pattern: callers can render a non-classified
        // view if the map never arrives.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const resolveCustomerFacilityIds = useCallback(async (customerId) => {
    if (!customerId) return [];
    const key = String(customerId);
    if (resolveCache.current.has(key)) return resolveCache.current.get(key);
    try {
      const res = await client.get(`/api/customers/${customerId}/resolved-facilities`);
      const ids = (res.data?.facilityIds || []).map(id => String(id));
      resolveCache.current.set(key, ids);
      return ids;
    } catch {
      return [];
    }
  }, []);

  return {
    customers,
    loading,
    resolveCustomerFacilityIds,
    allCustomerFacilityIds,
    facilityToMasterCustomerId,
  };
}

function sortCustomers(list) {
  // Group: master deal -> its sub-deals -> standalone customers.
  const byId = new Map(list.map(c => [c.id, c]));
  const masters = list.filter(c => !c.parentCustomerId);
  masters.sort((a, b) => a.name.localeCompare(b.name));

  const out = [];
  for (const m of masters) {
    out.push({ ...m, _depth: 0 });
    const subs = list
      .filter(c => c.parentCustomerId === m.id)
      .sort((a, b) => a.name.localeCompare(b.name));
    for (const s of subs) out.push({ ...s, _depth: 1 });
  }
  // Any orphan sub-deals whose parent isn't visible (e.g. permissions),
  // tack on at the bottom.
  const seen = new Set(out.map(c => c.id));
  for (const c of list) {
    if (!seen.has(c.id)) out.push({ ...c, _depth: c.parentCustomerId && !byId.has(c.parentCustomerId) ? 1 : 0 });
  }
  return out;
}

export default useCustomerFilter;
